import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Save, Search } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { ChoixListe } from "@/pages/parametres/ChoixListe";

const AUCUN = "__aucun__";
const ARCH = "proc-archives-nocrm";

const Recherche = ({ valeur, onChange, testId, tri, setTri }) => (
  <div className="flex gap-2">
    <label className="at-input flex items-center gap-2 flex-1 min-w-0"><Search size={15} className="shrink-0" />
      <input value={valeur} onChange={(e) => onChange(e.target.value)} placeholder="Rechercher" className="bg-transparent outline-none flex-1 min-w-0" data-testid={testId} /></label>
    {setTri && <button type="button" onClick={() => setTri(tri === "volume" ? "nom" : "volume")} className="at-btn at-btn-ghost shrink-0 !px-3 text-sm" data-testid={`${testId}-sort`}>{tri === "volume" ? "Tri : volume" : "Tri : A→Z"}</button>}
  </div>
);

const Pipeline = ({ p, i, e, defaut, setDefaut, etapes, setEtape }) => {
  const [ouvert, setOuvert] = useState(false);
  const optsProc = [{ code: AUCUN, label: "Aucun processus par défaut" }, { code: "archives", label: "Archives noCRM (créé automatiquement)" }, ...e.processus.map((x) => ({ code: x.id, label: x.nom }))];
  const optsEtapes = [...e.processus.flatMap((x) => x.etapes.map((s) => ({ code: `${x.id}::${s.code}`, label: `${x.nom} › ${s.nom}` }))),
    ...[["archive", "Archivé"], ["gagne", "Gagné"], ["perdu", "Perdu"]].map(([c, l]) => ({ code: `${ARCH}::${c}`, label: `Archives noCRM › ${l}` }))];
  const sans = p.etapes.filter((s) => !etapes(`${p.nom}${e.separateur}${s.nom}`)).length;
  return (
    <div className="at-soft p-3 space-y-2" data-testid={`json-pipeline-${i}`}>
      <button type="button" onClick={() => setOuvert(!ouvert)} className="w-full flex items-center justify-between gap-2 min-h-[44px] text-left" data-testid={`json-pipeline-toggle-${i}`}>
        <span className="min-w-0"><b className="break-words">{p.nom}</b><span className="text-xs at-muted block">{p.fiches} fiche(s) · {p.etapes.length} étape(s){sans ? ` · ${sans} sans correspondance` : ""}</span></span>
        <ChevronDown size={16} className={`shrink-0 transition-transform ${ouvert ? "rotate-180" : ""}`} />
      </button>
      <ChoixListe valeur={defaut || AUCUN} onChange={(v) => setDefaut(v === AUCUN ? null : v)} options={optsProc} testId={`json-pipeline-default-${i}`} />
      {ouvert && p.etapes.map((s, j) => {
        const k = `${p.nom}${e.separateur}${s.nom}`;
        const c = etapes(k);
        return (
          <div key={k} className="space-y-1" data-testid={`json-step-${i}-${j}`}>
            <p className="text-sm">{s.nom} <span className="at-muted">· {s.fiches}{c?.auto ? " · proposé" : ""}</span></p>
            <ChoixListe valeur={c ? `${c.processus_id}::${c.etape}` : ""} placeholder="À associer" options={optsEtapes} testId={`json-step-select-${i}-${j}`}
              onChange={(v) => { const [processus_id, etape] = v.split("::"); setEtape(k, { processus_id, etape }); }} />
          </div>
        );
      })}
    </div>
  );
};

export const CorrespJson = ({ e, onMaj }) => {
  const [pipes, setPipes] = useState(e.pipelines_defaut);
  const [et, setEt] = useState({});
  const [users, setUsers] = useState(e.utilisateurs);
  const [parts, setParts] = useState(e.partenaires);
  const [q, setQ] = useState("");
  const [qu, setQu] = useState("");
  const [tri, setTri] = useState("volume");
  const [envoi, setEnvoi] = useState(false);
  const liste = useMemo(() => {
    const l = e.resume.pipelines.filter((p) => p.nom.toLowerCase().includes(q.toLowerCase()) || p.etapes.some((s) => s.nom.toLowerCase().includes(q.toLowerCase())));
    return tri === "nom" ? [...l].sort((a, b) => a.nom.localeCompare(b.nom)) : l;
  }, [e.resume.pipelines, q, tri]);
  const etapes = (k) => et[k] || e.cibles[k];
  const changerDefaut = async (nom, v) => {
    setPipes({ ...pipes, [nom]: v });
    try { const r = await api.put("/migration-nocrm/json/correspondances", { pipelines: { [nom]: v } }); onMaj(r.data); }
    catch (x) { toast.error(messageErreur(x)); }
  };
  const enregistrer = async () => {
    setEnvoi(true);
    try { const r = await api.put("/migration-nocrm/json/correspondances", { pipelines: pipes, etapes: et, utilisateurs: users, partenaires: parts }); setEt({}); onMaj(r.data); toast.success("Correspondances enregistrées."); }
    catch (x) { toast.error(messageErreur(x)); } finally { setEnvoi(false); }
  };
  const optsUsers = [{ code: AUCUN, label: "Personne (fiche non attribuée)" }, ...e.utilisateurs_crm.map((u) => ({ code: u.id, label: u.nom }))];
  return (
    <div className="space-y-4" data-testid="json-mapping">
      <p className="text-sm">{e.resume.pipelines.length} pipelines, {e.resume.nb_etapes} étapes, {e.resume.utilisateurs.length} utilisateurs dans le fichier.
        {e.manquantes > 0 ? <b style={{ color: "var(--danger)" }} data-testid="json-missing"> {e.manquantes} étape(s) sans correspondance.</b> : <b style={{ color: "var(--success)" }} data-testid="json-missing"> Toutes les étapes ont une correspondance.</b>}</p>
      <p className="at-label">Pipelines et étapes</p>
      <Recherche valeur={q} onChange={setQ} testId="json-pipeline-search" tri={tri} setTri={setTri} />
      <div className="space-y-2">{liste.map((p, i) => <Pipeline key={p.nom} p={p} i={i} e={e} defaut={pipes[p.nom]} setDefaut={(v) => changerDefaut(p.nom, v)} etapes={etapes} setEtape={(k, v) => setEt({ ...et, [k]: v })} />)}</div>
      <p className="text-xs at-muted">Statuts : annulé → Perdu (motif de la catégorie « Annulation » ou « Annulé dans noCRM »), perdu → Perdu, gagné → étape gagnée du processus, à faire / en attente → l'étape associée.</p>
      <p className="at-label pt-2">Utilisateurs noCRM</p>
      <Recherche valeur={qu} onChange={setQu} testId="json-user-search" />
      {e.resume.utilisateurs.filter((u) => u.nom.toLowerCase().includes(qu.toLowerCase())).map((u, i) => (
        <div key={u.id} className="space-y-1" data-testid={`json-user-row-${i}`}>
          <p className="text-sm font-semibold break-words">{u.nom} <span className="at-muted font-normal">· {u.fiches} fiche(s)</span></p>
          <ChoixListe valeur={users[u.id] || AUCUN} options={optsUsers} testId={`json-user-select-${i}`} onChange={(v) => setUsers({ ...users, [u.id]: v === AUCUN ? null : v })} />
        </div>
      ))}
      {e.resume.partenaires.length > 0 && <p className="at-label pt-2">Partenaires noCRM</p>}
      {e.resume.partenaires.map((p, i) => (
        <div key={p.nom} className="space-y-1" data-testid={`json-partner-row-${i}`}>
          <p className="text-sm font-semibold">{p.nom} <span className="at-muted font-normal">· {p.fiches} fiche(s)</span></p>
          <ChoixListe valeur={parts[p.nom] || AUCUN} testId={`json-partner-select-${i}`} onChange={(v) => setParts({ ...parts, [p.nom]: v === AUCUN ? null : v })}
            options={[{ code: AUCUN, label: "Aucune société partenaire" }, ...e.partenaires_crm.map((x) => ({ code: x.id, label: x.nom }))]} />
        </div>
      ))}
      <p className="at-label pt-2">Champs personnalisés</p>
      <div className="flex flex-wrap gap-1.5" data-testid="json-fields">
        {e.champs.map((c) => <span key={c.nom} className="text-[11px] at-pill px-2 py-1" style={{ background: "var(--surface-2)" }}>{c.nom} · {c.fiches}{c.existe ? "" : " · sera créé"}</span>)}
      </div>
      <p className="text-xs at-muted">Nom, téléphone et adresse des travaux vont sur le contact ; les autres champs deviennent des champs « noCRM – … » de la fiche.</p>
      <button type="button" onClick={enregistrer} disabled={envoi} className="at-btn at-btn-primary w-full" data-testid="json-save-mapping"><Save size={16} />Enregistrer les correspondances</button>
    </div>
  );
};
